import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Avatar, Button, Dropdown, Input, Table, Tooltip } from "antd";
import { Apartment, MoreVert, Report, SearchOutlined } from "@mui/icons-material";
import * as CompanyActions from "../../../../store/actions/company";
import { useHistory } from "react-router-dom";
import NoCompanySymbol from "../../../../assets/images/nocompany.png";
import { PlusOutlined, UserOutlined } from "@ant-design/icons";
import { getInitials } from "../../../../utils";
import {
  DuplicateCompanyModal,
  DeleteCompanyModal,
  AddModal,
} from "../company-submission/components/modal";
import "./styles.scss";

const MyCompanies = () => {
  // STATES
  const [searchVal, setSearchVal] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deleteModalPreview, setDeleteModalPreview] = useState(false);
  const [editModalPreview, setEditModalPreview] = useState(false);
  const [duplicateModalPreview, setDuplicateModalPreview] = useState(false);
  const [createModalPreview, setCreateModalPreview] = useState(false);

  // CONST VALS
  const history = useHistory();
  const dispatch = useDispatch();

  // REDUX DATA
  const companies = useSelector((state) => state.CompanyReducer.companies);
  const user = useSelector((state) => state.UserReducer.profile);

  // HELPERS
  const generateMenu = (data) => {
    const items = [
      {
        key: "1",
        label: "Edit Company",
        onClick: (e) => {
          e.domEvent.stopPropagation();
          selectCompany(data);
        },
      },
      {
        key: "2",
        label: "View Tasks",
        onClick: (e) => {
          e.domEvent.stopPropagation();
          viewTasks(data);
        },
      },
      {
        key: "3",
        label: "Duplicate",
        onClick: (e) => {
          e.domEvent.stopPropagation();
          duplicateCompanyPreviewHandler(data);
        },
      },
    ];

    if (data?.created_by_user) {
      items.push(
        {
          key: "4",
          label: "Edit",
          onClick: (e) => {
            e.domEvent.stopPropagation();
            editCompanyPreviewHandler(data);
          },
        },
        {
          key: "5",
          label: "Delete",
          onClick: (e) => {
            e.domEvent.stopPropagation();
            deleteCompanyPreviewHandler(data);
          },
          danger: true,
        }
      );
    }

    return items;
  };

  const generateColumns = () => {
    const columns = [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        sorter: (a, b) => a.company_name.localeCompare(b.company_name),
        render: (_, record) => (
          <span className="title-cell">
            <span className="thumb">
              <Apartment />
            </span>
            <span className="name">{record?.company_name}</span>
            {!record?.user_input && (
              <span className="tagline error">
                <Tooltip
                  title="Company details not filled out"
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                >
                  {/* incomplete company details */}
                  <Report />
                </Tooltip>
              </span>
            )}
          </span>
        ),
      },
      {
        title: "Role",
        width: 200,
        dataIndex: "created_by_user",
        key: "created_by_user",
        sorter: (a, b) => a.created_by_user - b.created_by_user,
        render: (data) => {
          return <span className="role-cell">{data ? "Owner" : "Editor"}</span>;
        },
      },
      {
        title: "Owner",
        dataIndex: "created_by",
        key: "created_by",
        sorter: (a, b) => a.created_by?.username.localeCompare(b.created_by?.username),
        render: (data, record) => {
          const isUserOwner = record.created_by_user;
          const hasUserName = data?.username && data?.username.length > 0;
          return (
            <span className="owner-cell">
              <span className="thumb">
                <Avatar src={data?.profile_picture} shape="circle" size="medium">
                  {hasUserName ? getInitials(data?.username) : <UserOutlined />}
                </Avatar>
              </span>
              <span className="name">{isUserOwner ? "Me" : data?.username}</span>
            </span>
          );
        },
      },
    ];

    if (user.organization) {
      columns.push({
        title: "Acccess type",
        dataIndex: "access_type",
        key: "access_type",
        sorter: (a, b) => a.shared - b.shared,
        render: (_, record) => {
          return (
            <span className="access-type-cell">
              {!record?.shared
                ? "Personal"
                : record?.created_by_user
                ? "Shared by me"
                : "Shared with me"}
            </span>
          );
        },
      });
    }

    columns.push({
      title: "",
      width: 80,
      dataIndex: "",
      key: "actions",
      render: (data) => (
        <span className="actions">
          <Dropdown
            menu={{
              items: generateMenu(data),
            }}
            trigger={["click"]}
            placement="bottomRight"
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          >
            <span className="action">
              <MoreVert />
            </span>
          </Dropdown>
        </span>
      ),
    });

    return columns;
  };

  // CONST VALS
  const columns = generateColumns();

  // HANDLERS
  const selectCompany = (company) => {
    dispatch(CompanyActions.setSelectedCompany(company));
    history.replace("/company-submission");
  };

  const viewTasks = (company) => {
    dispatch(CompanyActions.setSelectedCompany(company));
    history.replace("/company-tasks");
  };

  const duplicateCompanyPreviewHandler = (company) => {
    setSelectedCompany(company);
    setDuplicateModalPreview(true);
  };

  const editCompanyPreviewHandler = (company) => {
    setSelectedCompany(company);
    setEditModalPreview(true);
  };

  const deleteCompanyPreviewHandler = (company) => {
    setSelectedCompany(company);
    setDeleteModalPreview(true);
  };

  // HOOKS
  useEffect(() => {
    if (!searchVal) {
      setFilteredData(companies);
      return;
    }
    const filtered = companies.filter((company) =>
      company.company_name.toLowerCase().includes(searchVal.toLowerCase())
    );
    setFilteredData(filtered);
  }, [companies, searchVal]);

  return (
    <div className="my-companies">
      <h2 className="page-title">Companies</h2>
      <div className="companies-listing">
        <div className="top-header">
          <div className={`input-box`}>
            <span className="prefix">
              <SearchOutlined />
            </span>
            <Input
              value={searchVal}
              placeholder="Name"
              onChange={(e) => setSearchVal(e.target.value)}
            />
          </div>
          <Button
            type="primary"
            className="btn btn-primary"
            onClick={() => setCreateModalPreview(true)}
            icon={<PlusOutlined />}
          >
            Add Company
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          scroll={{
            y: 550,
            x: "max-content",
          }}
          locale={{
            emptyText: (
              <div className="empty-box">
                <img src={NoCompanySymbol} height={60} width={60} alt="" />
                <p>No company found</p>
              </div>
            ),
          }}
        />
      </div>
      {createModalPreview && (
        <AddModal
          show={createModalPreview}
          onClose={() => setCreateModalPreview(false)}
          isOrganization={user.organization}
        />
      )}
      {editModalPreview && (
        <AddModal
          isEdit={true}
          data={selectedCompany}
          show={editModalPreview}
          onClose={() => setEditModalPreview(false)}
          currentCompany={selectedCompany}
          isOrganization={user.organization}
        />
      )}
      <DuplicateCompanyModal
        preview={duplicateModalPreview}
        selectedCompany={selectedCompany}
        previewHandler={() => setDuplicateModalPreview(false)}
      />
      <DeleteCompanyModal
        preview={deleteModalPreview}
        selectedCompany={selectedCompany}
        previewHandler={() => setDeleteModalPreview(false)}
      />
    </div>
  );
};

export default MyCompanies;
